/**
 * **主题注册表** —— 内置主题 + 用户主题 + 当前选中项。
 *
 * ## 存什么
 *
 * ```text
 *   localStorage['node-print.themes.v1']   →  用户主题（整份文档，含 css）
 *   localStorage['node-print.theme.v1']    →  当前选中的 id
 * ```
 *
 * ## ⚠️ 从旧键迁移
 *
 * 改动前只有一个布尔：`localStorage['node-print.theme.v1'] === 'light'`。
 * **同一个键现在存 id**，所以读的时候要认三种情况：
 *
 * ```text
 *   'light'   → 旧数据，映射到 paper
 *   'dark'    → 旧数据，映射到 graphite
 *   '<id>'    → 新数据
 * ```
 *
 * 不做迁移的后果是「用户升级后主题被重置回默认」—— 看起来像设置丢了。
 * 用户明确说过「主题要记住」，所以这一步不能省。
 */
import { computed, ref, shallowRef, type ComputedRef, type Ref } from 'vue'
import type { ResolvedTheme, ThemeDocument, ThemeSummary } from '../types/theme'
import { BUILTIN_THEMES, DEFAULT_THEME_ID } from './builtin'
import { normalizeTheme, resolveTheme, validateTheme } from './themeDoc'

const ACTIVE_KEY = 'node-print.theme.v1'
const USER_KEY = 'node-print.themes.v1'

/** 旧布尔值到新主题 id 的映射 */
const LEGACY: Record<string, string> = { light: 'paper', dark: 'graphite' }

function safeGet(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage?.setItem(key, value)
  } catch {
    /* 隐私模式 / 配额满 —— 静默降级，不影响本次会话 */
  }
}

/* ── 用户主题 ─────────────────────────────────────────────────── */

function loadUserThemes(): ThemeDocument[] {
  const raw = safeGet(USER_KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((x): x is ThemeDocument => !!x && typeof x === 'object' && 'id' in x && 'name' in x)
      .map(normalizeTheme)
  } catch {
    return []
  }
}

/** 用户主题（可写）。内置主题不在这里 */
export const userThemes: Ref<ThemeDocument[]> = shallowRef(loadUserThemes())

function persistUserThemes(): void {
  safeSet(USER_KEY, JSON.stringify(userThemes.value))
}

/** 内置 + 用户，内置在前 */
export const allThemes: ComputedRef<ThemeDocument[]> = computed(() => [
  ...BUILTIN_THEMES,
  ...userThemes.value,
])

const byId = computed(() => new Map(allThemes.value.map((t) => [t.id, t])))

export function findTheme(id: string): ThemeDocument | undefined {
  return byId.value.get(id)
}

/** 是不是内置主题（内置的删不掉、也改不了） */
export function isBuiltinTheme(id: string): boolean {
  return BUILTIN_THEMES.some((t) => t.id === id)
}

/** 菜单 / 画廊用的摘要 */
export const themeSummaries: ComputedRef<ThemeSummary[]> = computed(() =>
  allThemes.value.map((t) => ({
    id: t.id,
    name: t.name,
    hint: t.hint,
    scheme: t.scheme ?? 'dark',
    builtin: isBuiltinTheme(t.id),
    extends: t.extends,
  })),
)

/* ── 当前主题 ─────────────────────────────────────────────────── */

function readActiveId(): string {
  const raw = safeGet(ACTIVE_KEY)
  if (!raw) return DEFAULT_THEME_ID
  const migrated = LEGACY[raw] ?? raw
  return byId.value.has(migrated) ? migrated : DEFAULT_THEME_ID
}

export const activeThemeId: Ref<string> = ref(readActiveId())

/**
 * **预览中的草稿**。
 *
 * ⚠️ 主题编辑器里每改一个色都要立刻看到效果 —— 否则「调主题」就变成了
 * 「猜 + 保存 + 看 + 再猜」。但草稿**不能**直接写进 `userThemes`：
 * 那样每拖一次取色器就往 localStorage 里塞一份主题，而且「取消」变得没法实现。
 *
 * 所以草稿住在这里：`resolvedTheme` 优先读它，`applyTheme` 照常应用 ——
 * 整条渲染链一行都不用改。`null` = 没有草稿，用正式选中的那套。
 */
export const previewDoc: Ref<ThemeDocument | null> = shallowRef(null)

/** 开始预览一份草稿（编辑器的入口） */
export function beginPreview(doc: ThemeDocument): void {
  previewDoc.value = normalizeTheme(doc)
}

/** 结束预览，回到正式选中的主题 */
export function endPreview(): void {
  previewDoc.value = null
}

/**
 * 当前主题的**已解析**结果。
 *
 * ⚠️ 解析失败时**退回默认主题**而不是抛错。理由：主题来自 localStorage，
 * 一份写坏的用户主题不该让整个编辑器打不开。问题通过
 * `themeProblems` 暴露到界面上。
 */
export const themeProblems: Ref<string[]> = shallowRef([])

export const resolvedTheme: ComputedRef<ResolvedTheme> = computed(() => {
  const doc =
    previewDoc.value ?? byId.value.get(activeThemeId.value) ?? BUILTIN_THEMES[0]!
  const { theme, problems } = resolveTheme(doc, (id) => byId.value.get(id))
  /* computed 里不该有副作用 —— 但这条只是「把问题记下来给界面看」，
     不参与渲染结果，所以可接受；写到别处反而要再造一个 watch。 */
  if (problems.length && themeProblems.value.join('|') !== problems.join('|')) {
    queueMicrotask(() => {
      themeProblems.value = problems
    })
  }
  return theme ?? fallbackResolved()
})

function fallbackResolved(): ResolvedTheme {
  const r = resolveTheme(BUILTIN_THEMES[0]!, () => undefined)
  /* 内置主题解析不出来是代码 bug，不是数据问题 */
  return r.theme!
}

export function setActiveTheme(id: string): void {
  const next = byId.value.has(id) ? id : DEFAULT_THEME_ID
  activeThemeId.value = next
  safeSet(ACTIVE_KEY, next)
}

/* ── 增删改 ───────────────────────────────────────────────────── */

export interface SaveResult {
  ok: boolean
  problems: string[]
}

/**
 * 存一份用户主题（同 id 覆盖）。
 *
 * ⚠️ **不许覆盖内置主题** —— 覆盖了就没法「恢复默认」，
 * 而用户下次想回到开箱观感时会找不到路。
 */
export function saveUserTheme(doc: ThemeDocument): SaveResult {
  const normalized = normalizeTheme(doc)
  const problems = validateTheme(
    normalized,
    allThemes.value.filter((t) => t.id !== normalized.id).map((t) => t.id),
  )
  if (isBuiltinTheme(normalized.id)) {
    problems.push(`\`${normalized.id}\` 是内置主题的 id，请换一个（内置主题不能被覆盖）`)
  }
  if (problems.length) return { ok: false, problems }

  const list = [...userThemes.value]
  const at = list.findIndex((t) => t.id === normalized.id)
  if (at >= 0) list[at] = normalized
  else list.push(normalized)
  userThemes.value = list
  persistUserThemes()
  return { ok: true, problems: [] }
}

export function removeUserTheme(id: string): boolean {
  if (isBuiltinTheme(id)) return false
  const list = userThemes.value.filter((t) => t.id !== id)
  if (list.length === userThemes.value.length) return false
  userThemes.value = list
  persistUserThemes()
  /* 删掉的正好是当前主题 → 退回默认，避免界面卡在没有的主题上 */
  if (activeThemeId.value === id) setActiveTheme(DEFAULT_THEME_ID)
  return true
}

/**
 * 以某个主题为蓝本造一份可编辑的副本。
 *
 * ⚠️ 副本必须**把继承链展开**（`extends` 清掉、字段并进来），
 * 否则用户改完存下来，而祖先主题是内置的 —— 他删不掉祖先，
 * 也没法预知祖先以后会不会变。
 */
export function forkTheme(id: string, newId: string, newName: string): ThemeDocument | null {
  const { theme } = resolveTheme(findTheme(id) ?? BUILTIN_THEMES[0]!, (x) => byId.value.get(x))
  if (!theme) return null
  return normalizeTheme({
    ...theme.doc,
    id: newId,
    name: newName,
    extends: undefined,
    tokens: { ...theme.tokens },
    recipes: { ...theme.recipes },
    layout: structuredCloneSafe(theme.layout),
    css: theme.css || undefined,
  })
}

/** `structuredClone` 在老 Safari 上没有 —— 这里只处理纯数据，用 JSON 就够 */
function structuredCloneSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}
